import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CloudUpload, Check, Error, Info } from '@mui/icons-material';
import { importFromGoogleDrive, getImportStatus, cancelImport } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface ImportProgress {
  status: 'idle' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  percentage: number;
  details: string;
  // Original fields
  current_step?: string;
  total_steps?: number;
  completed_steps?: number;
  errors?: string[];
  warning_count?: number;
  error_count?: number;
  timestamp?: string;
  current_page?: number | string;
  current_question?: number;
  // New fields from database model
  job_id?: number;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
  // Question import progress
  total_items?: number;
  processed_items?: number;
  skipped_items?: number;
  // Media file upload progress
  total_media_files?: number;
  processed_media_files?: number;
  skipped_media_files?: number;
  media_progress_percentage?: number;
  overall_progress_percentage?: number;
  error?: string;
}

interface CSVValidationResult {
  valid: boolean;
  error: string;
  missing_columns: string[];
  available_columns: string[];
  column_mapping?: {
    [key: string]: string | string[];
  };
}

interface ErrorDetails {
  error_type: string;
  message: string;
  help?: string;
  found_files?: string[];
  file_name?: string;
  file_id?: string;
  folder_id?: string;
  provided_url?: string;
  traceback?: string;
  validation_result?: CSVValidationResult;
  [key: string]: any; // Allow for additional properties
}

interface ImportSummary {
  total_files_found: number;
  media_files_processed: number;
  media_files_downloaded: number;
  media_files_failed: number;
  csv_file: string;
  file_limit_applied: boolean;
}

interface ImportResult {
  success: boolean;
  error?: string;
  details?: ErrorDetails;
  progress?: ImportProgress;
  validation_result?: CSVValidationResult;
  summary?: ImportSummary;
  // New field for job ID from async import
  import_job_id?: number;
  // Optional message field for status messages
  message?: string;
  // Course information
  course?: {
    name: string;
    description: string;
  };
  // Statistics from completed import
  statistics?: {
    questions_processed: number;
    flows_created: number;
    flows_updated: number;
    questions_created: number;
    questions_skipped: number;
    errors: number;
  };
}

const ContentImport: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [driveUrl, setDriveUrl] = useState('');
  const [courseName, setCourseName] = useState('Life Plan');
  const [courseDescription, setCourseDescription] = useState('');
  const [skipMediaDownload, setSkipMediaDownload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [lastValidProgress, setLastValidProgress] = useState<ImportProgress | null>(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);

  // Function to stop polling for updates
  const handleStopPolling = useCallback(() => {
    setPollingActive(false);
  }, []);

  // Function to fetch the current import status
  const fetchImportStatus = useCallback(async () => {
    try {
      // If we have a job ID from a previous import, use that
      const jobId = result?.import_job_id || progress?.job_id;
      
      // Get the latest timestamp for conditional updates
      const sinceTimestamp = progress?.updated_at;
      
      // Fetch the current import status
      const statusData = await getImportStatus(jobId, sinceTimestamp);
      
      // If the server indicates no updates, don't update the UI
      if (statusData.updated === false) {
        console.log('No updates since last check');
        return;
      }
      
      // Only update UI if we have valid data
      if (statusData) {
        // Update progress if the new data shows advancement in any progress metric or status change
        if (!lastValidProgress || 
            statusData.percentage > (lastValidProgress.percentage || 0) || 
            statusData.media_progress_percentage > (lastValidProgress.media_progress_percentage || 0) || 
            statusData.processed_media_files > (lastValidProgress.processed_media_files || 0) || 
            statusData.status !== lastValidProgress.status) {
          
          console.log('Updating progress with new data:', statusData);
          // Store this as the last valid progress
          setLastValidProgress(statusData);
          // Update the displayed progress
          setProgress(statusData);
        }
        
        // If the import is completed, failed, or cancelled, check if there's actual progress before stopping polling
        if (statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'cancelled') {
          // Check if this is a premature completion with no actual progress
          // For cancelled status, we always consider it has progress if there are processed media files
          const hasProgress = statusData.processed_items > 0 || statusData.processed_media_files > 0 || statusData.status === 'cancelled';
          const hasError = statusData.error_count > 0 || statusData.error;
          
          // Only stop polling if there's actual progress or an error
          if (hasProgress || hasError) {
            console.log('Import completed or failed with progress or errors, stopping polling');
            setPollingActive(false);
          } else {
            console.log('Import marked as completed but has no progress, continuing to poll');
            // Continue polling as this might be a premature completion
          }
        }
      }
    } catch (err) {
      console.error('Error fetching import status:', err);
    }
  }, [lastValidProgress, result]);
  
  // Handler for cancelling the import process
  const handleCancelImport = useCallback(async () => {
    if (!progress?.job_id) {
      console.error('No active import job to cancel');
      return;
    }
    
    try {
      // Show confirmation dialog
      if (!window.confirm('Are you sure you want to cancel this import? This will stop the import process immediately.')) {
        return;
      }
      
      setLoading(true);
      const result = await cancelImport(progress.job_id);
      console.log('Import cancelled:', result);
      
      // Force an immediate status update
      await fetchImportStatus();
      
      // Show success message
      setResult({
        success: true,
        message: 'Import cancelled successfully'
      });
    } catch (error) {
      console.error('Error cancelling import:', error);
      setResult({
        success: false,
        error: 'Failed to cancel import'
      });
    } finally {
      setLoading(false);
    }
  }, [progress?.job_id, fetchImportStatus]);

  // Set up polling with a fixed 5-second interval
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (pollingActive) {
      // Use a consistent 5-second polling interval as requested
      const pollingInterval = 5000; // 5 seconds
      
      console.log(`Setting up polling with fixed interval: ${pollingInterval}ms`);
      
      // Set up the interval with the fixed polling interval
      intervalId = setInterval(fetchImportStatus, pollingInterval);
      
      // Delay the first fetch by 10 seconds to give the backend time to initialize
      console.log('Delaying initial import status check by 10 seconds...');
      setTimeout(fetchImportStatus, 10000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pollingActive, fetchImportStatus, lastValidProgress]);

  const handleImport = async () => {
    if (!driveUrl) {
      setError('Please enter a Google Drive URL');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setProgress(null);
    setLastValidProgress(null);

    try {
      console.log('Starting import from Google Drive:', driveUrl);
      
      // Initiate the import (now returns immediately with job ID)
      const response = await importFromGoogleDrive({
        drive_url: driveUrl,
        course_name: courseName,
        course_description: courseDescription || undefined,
        skip_media_download: skipMediaDownload,
      });

      console.log('Import initiated successfully, response:', response);
      
      // Store the result
      setResult(response);
      
      // Create initial progress object if none was returned
      // The backend now returns immediately, so we need to create a progress object
      // to show the user that the import has started
      const initialProgress: ImportProgress = {
        job_id: response.import_job_id,
        status: 'in_progress',
        percentage: 0,
        total_items: 0,
        processed_items: 0,
        details: `Starting import of ${courseName}...`,
        updated_at: new Date().toISOString()
      };
      
      setProgress(initialProgress);
      setLastValidProgress(initialProgress);
      
      // Start polling for status updates
      setPollingActive(true);
      
      // Show a success message
      setError(null);
    } catch (err: any) {
      console.error('Error initiating import:', err);
      setError(err.message || 'An error occurred during import');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.is_staff) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Access Denied</AlertTitle>
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Content Import
      </Typography>
      <Typography variant="body1" paragraph>
        Import content from Google Drive. The folder should contain a CSV file with question data and media files.
      </Typography>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Import from Google Drive
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Enter the URL of a Google Drive folder that contains your content CSV file and media assets.
          The folder must be shared directly with the service account email (not with "Anyone with the link").
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Google Drive Folder URL"
              variant="outlined"
              value={driveUrl}
              onChange={(e) => setDriveUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              helperText="Example: https://drive.google.com/drive/folders/1d6Zsl3UXA8XHayHCk1Ss83ImCXTRF7gT?usp=drive_link"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Course Name"
              variant="outlined"
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              helperText="Name of the course to create or update"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Course Description (Optional)"
              variant="outlined"
              value={courseDescription}
              onChange={(e) => setCourseDescription(e.target.value)}
              helperText="Description of the course"
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={skipMediaDownload}
                    onChange={(e) => setSkipMediaDownload(e.target.checked)}
                    color="primary"
                  />
                }
                label="Skip media file downloads"
              />
              <Tooltip title="Enable this if media files are already in cloud storage. This will significantly speed up the import process.">
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
              Enable this option if media files are already in cloud storage. This will significantly speed up the import process.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
              onClick={handleImport}
              disabled={loading || !driveUrl}
              sx={{ mt: 2 }}
            >
              {loading ? 'Importing...' : 'Import Content'}
            </Button>
          </Grid>
        </Grid>

        {/* Progress Display - Always visible */}
        {
          <Box sx={{ mt: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">
                Import Progress
              </Typography>
              <Box>
                {/* Show Cancel Import button when import is in progress */}
                {progress?.status === 'in_progress' && progress?.job_id && (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    size="small" 
                    onClick={handleCancelImport}
                    startIcon={<Error />}
                    sx={{ mr: 1 }}
                    disabled={loading}
                  >
                    Cancel Import
                  </Button>
                )}
                
                {/* Button to stop polling for updates */}
                {pollingActive && (
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    size="small" 
                    onClick={handleStopPolling}
                  >
                    Stop Updates
                  </Button>
                )}
              </Box>
            </Box>
            
            {/* Progress bars - Only showing Question Import and Media File Upload */}
            
            {/* Question Import Progress */}
            <Typography variant="body2" fontWeight="medium">
              Question Import Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress?.percentage || 0} 
              sx={{ height: 10, borderRadius: 5, mb: 1, bgcolor: theme => theme.palette.grey[200] }}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2">
                {progress?.current_step || 'Starting import...'}
                {progress?.current_page && ` (Page ${progress.current_page})`}
                {progress?.current_question && ` - Question ${progress.current_question}`}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {progress?.processed_items !== undefined && progress?.total_items ? 
                  `${progress.processed_items}/${progress.total_items} questions (${progress.percentage || 0}%)` : 
                  `${progress?.percentage || 0}%`}
              </Typography>
            </Box>
            
            {/* Media File Upload Progress - always show regardless of media file status */}
            <Typography variant="body2" fontWeight="medium">
              Media File Upload Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress?.media_progress_percentage || 0} 
              sx={{ height: 10, borderRadius: 5, mb: 1, bgcolor: theme => theme.palette.grey[200] }}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2">
                {(progress?.processed_media_files ?? 0) > 0 ? 
                  progress?.status === 'cancelled' ? 
                    `Media upload cancelled` : 
                    `Uploading media files...` : 
                  `Waiting for media files...`}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {progress?.total_media_files ? 
                  `${progress.processed_media_files || 0}/${progress.total_media_files} files (${progress.media_progress_percentage || 0}%)` : 
                  `0%`}
              </Typography>
            </Box>
            
            {progress?.details && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {progress.details}
              </Typography>
            )}
            {/* Display warnings and errors separately */}
            {progress?.errors && progress.errors.length > 0 && (
              <>
                {/* Show warnings */}
                {(progress.warning_count ?? 0) > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Warnings ({progress.warning_count ?? 0})</AlertTitle>
                    <List dense>
                      {progress.errors
                        .filter(err => err.startsWith('[WARNING]'))
                        .map((err, index) => (
                          <ListItem key={`warning-${index}`}>
                            <ListItemText primary={err.replace('[WARNING] ', '')} />
                          </ListItem>
                        ))}
                    </List>
                  </Alert>
                )}
                
                {/* Show errors */}
                {(progress.error_count ?? 0) > 0 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <AlertTitle>Errors ({progress.error_count ?? 0})</AlertTitle>
                    
                    {/* Group errors by type to make them more manageable */}
                    {(() => {
                      // Extract all non-warning errors
                      const errors = progress.errors.filter(err => !err.startsWith('[WARNING]'));
                      
                      // Check for common error patterns
                      const downloadErrors = errors.filter(err => err.includes('Failed to download') || err.includes('download failed'));
                      const validationErrors = errors.filter(err => err.includes('validation') || err.includes('invalid'));
                      const permissionErrors = errors.filter(err => err.includes('permission') || err.includes('access denied'));
                      const otherErrors = errors.filter(err => 
                        !downloadErrors.includes(err) && 
                        !validationErrors.includes(err) && 
                        !permissionErrors.includes(err)
                      );
                      
                      // Show error summary if there are many errors
                      const showSummary = errors.length > 10;
                      
                      return (
                        <>
                          {showSummary && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Error Summary:</Typography>
                              <Typography variant="body2">
                                • {downloadErrors.length} media download errors
                                {downloadErrors.length > 0 && " (check file permissions and connectivity)"}
                              </Typography>
                              <Typography variant="body2">
                                • {validationErrors.length} data validation errors
                                {validationErrors.length > 0 && " (check CSV format and content)"}
                              </Typography>
                              <Typography variant="body2">
                                • {permissionErrors.length} permission errors
                                {permissionErrors.length > 0 && " (check Google Drive sharing settings)"}
                              </Typography>
                              <Typography variant="body2">
                                • {otherErrors.length} other errors
                              </Typography>
                            </Box>
                          )}
                          
                          <List dense sx={{ maxHeight: '300px', overflow: 'auto' }}>
                            {errors.map((err, index) => (
                              <ListItem key={`error-${index}`}>
                                <ListItemText 
                                  primary={err} 
                                  primaryTypographyProps={{ 
                                    style: { 
                                      whiteSpace: 'normal', 
                                      wordBreak: 'break-word' 
                                    } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      );
                    })()}
                  </Alert>
                )}
              </>
            )}
          </Box>
        }

        {/* Display specialized error messages based on error type */}
        {result?.details?.error_type === 'missing_csv' && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>No CSV File Found</AlertTitle>
            <Typography variant="body2" paragraph>
              No CSV file was found in the Google Drive folder. Please ensure your folder contains at least one CSV file with the required content structure.
            </Typography>
            
            {result.details.found_files && result.details.found_files.length > 0 ? (
              <>
                <Typography variant="subtitle2">Files found in the folder:</Typography>
                <Box component="ul" sx={{ mt: 1, mb: 2 }}>
                  {result.details.found_files.map((file, index) => (
                    <li key={index}><Typography variant="body2">{file}</Typography></li>
                  ))}
                </Box>
                <Typography variant="body2">
                  None of these files have a .csv extension. Please add a CSV file to the folder and try again.
                </Typography>
              </>
            ) : (
              <Typography variant="body2">
                No files were found in the folder. Please check that you've shared the correct folder and it contains the necessary files.
              </Typography>
            )}
            
            <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="subtitle2">Tips:</Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Ensure your CSV file has a .csv extension</li>
                <li>Make sure the Google Drive folder is shared directly with the service account email (not with "Anyone with the link")</li>
                <li>Check that the URL you entered is correct</li>
              </Typography>
            </Box>
          </Alert>
        )}
        
        {/* Display specialized error for CSV validation failures */}
        {result?.details?.error_type === 'csv_validation_failed' && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>CSV Validation Failed</AlertTitle>
            <Typography variant="body2" paragraph>
              {result.details.message}
            </Typography>
            
            {result.details.validation_result && (
              <>
                {result.details.validation_result.missing_columns && result.details.validation_result.missing_columns.length > 0 && (
                  <>
                    <Typography variant="subtitle2">Missing Required Columns:</Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 2 }}>
                      {result.details.validation_result.missing_columns.map((col, index) => (
                        <li key={index}><Typography variant="body2">{col}</Typography></li>
                      ))}
                    </Box>
                  </>
                )}
                
                {result.details.validation_result.column_mapping && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Detected Column Mapping:</Typography>
                    <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                      <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', overflowX: 'auto' }}>
                        {Object.entries(result.details.validation_result.column_mapping).map(([key, value]) => (
                          `${key}: ${Array.isArray(value) ? value.join(' or ') : value}
`
                        ))}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            )}
            
            <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="subtitle2">CSV Format Requirements:</Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Required columns: <code>Flow</code>, <code>Question type</code>, <code>Text</code> (or <code>LowText</code> and <code>HighText</code>), <code>Page</code></li>
                <li>For multiple choice questions, include columns named <code>Option1</code>, <code>Option2</code>, etc.</li>
                <li>For rating questions, include <code>LowText</code> and <code>HighText</code> columns for the scale endpoints</li>
                <li>Make sure all column names are spelled correctly and have no extra spaces</li>
              </Typography>
            </Box>
          </Alert>
        )}
        
        {/* Display generic error if not handled by specialized error components */}
        {(error || result?.details?.error_type === 'general_error' || (result?.details && !['missing_csv', 'csv_validation_failed'].includes(result?.details?.error_type))) && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>Error</AlertTitle>
            <Typography variant="body2" paragraph>
              {result?.details?.message || error}
            </Typography>
            
            {result?.details?.help && (
              <Typography variant="body2" paragraph>
                <strong>Suggestion:</strong> {result.details.help}
              </Typography>
            )}
            
            {result?.details?.error_type && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="subtitle2">Error Details:</Typography>
                <Typography variant="body2" component="pre" sx={{ mt: 1, overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  Error Type: {result.details.error_type}
                  {Object.entries(result.details)
                    .filter(([key]) => !['error_type', 'message', 'help'].includes(key))
                    .map(([key, value]) => (
                      `
${key}: ${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}`
                    ))
                  }
                </Typography>
              </Box>
            )}
          </Alert>
        )}
        
        {result?.validation_result && !result.validation_result.valid && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>CSV Validation Failed</AlertTitle>
            <Typography variant="body2" paragraph>
              {result.validation_result.error}
            </Typography>
            
            {result.validation_result.missing_columns.length > 0 && (
              <>
                <Typography variant="subtitle2">Missing Required Columns:</Typography>
                <Box component="ul" sx={{ mt: 1, mb: 2 }}>
                  {result.validation_result.missing_columns.map((col, index) => (
                    <li key={index}><Typography variant="body2">{col}</Typography></li>
                  ))}
                </Box>
              </>
            )}
            
            <Typography variant="subtitle2">Available Columns:</Typography>
            <Box sx={{ mt: 1, mb: 1, maxHeight: 150, overflowY: 'auto', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
                {result.validation_result.available_columns.join(', ')}
              </Typography>
            </Box>
            
            {result.validation_result.column_mapping && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Column Mapping:</Typography>
                <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', overflowX: 'auto' }}>
                    {Object.entries(result.validation_result.column_mapping).map(([key, value]) => (
                      `${key}: ${Array.isArray(value) ? value.join(' or ') : value}
`
                    ))}
                  </Typography>
                </Box>
              </>
            )}
            
            <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="subtitle2">CSV Requirements:</Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Required columns: <code>Flow</code>, <code>Question type</code>, <code>Text</code> (or <code>LowText</code> and <code>HighText</code>), <code>Page</code></li>
                <li>For multiple choice questions, include columns named <code>Option1</code>, <code>Option2</code>, etc.</li>
                <li>For rating questions, include <code>LowText</code> and <code>HighText</code> columns for the scale endpoints</li>
                <li>Make sure all column names are spelled correctly and have no extra spaces</li>
              </Typography>
            </Box>
            
            <Typography variant="body2" sx={{ mt: 2 }}>
              Please ensure your CSV file has all the required columns and follows the expected format.
            </Typography>
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Import Results
            </Typography>

            {result.success ? (
              <Card sx={{ backgroundColor: result.statistics ? theme.palette.success.light : theme.palette.info.light, 
                         color: result.statistics ? theme.palette.success.contrastText : theme.palette.info.contrastText }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    {result.statistics ? <Check sx={{ mr: 1 }} /> : <CloudUpload sx={{ mr: 1 }} />}
                    <Typography variant="h6">{result.statistics ? 'Import Successful' : 'Import Started'}</Typography>
                  </Box>
                  
                  {!result.statistics && (
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      The import process has been started and is running in the background. You can track its progress above.
                      This may take several minutes depending on the amount of content being imported.
                    </Typography>
                  )}
                  
                  {/* Display file processing summary */}
                  {result.summary && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="subtitle1">File Processing Summary</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary={`Total Files Found: ${result.summary.total_files_found}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Media Files Processed: ${result.summary.media_files_processed}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Successfully Downloaded: ${result.summary.media_files_downloaded}`} />
                        </ListItem>
                        {result.summary.media_files_failed > 0 && (
                          <ListItem>
                            <ListItemText 
                              primary={`Failed Downloads: ${result.summary.media_files_failed}`} 
                              primaryTypographyProps={{ color: 'error' }}
                            />
                          </ListItem>
                        )}
                        {result.summary.file_limit_applied && (
                          <ListItem>
                            <ListItemText 
                              primary={`Note: Only processed ${result.summary.media_files_processed} out of ${result.summary.total_files_found - 1} media files due to file limit.`}
                              primaryTypographyProps={{ color: 'warning.main' }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}
                  
                  {/* Display content statistics */}
                  {result.statistics && (
                    <>
                      <Typography variant="subtitle1">Content Statistics</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary={`Questions Processed: ${result.statistics.questions_processed}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Flows Created: ${result.statistics.flows_created}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Flows Updated: ${result.statistics.flows_updated}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Questions Created: ${result.statistics.questions_created}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Questions Skipped: ${result.statistics.questions_skipped}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary={`Errors: ${result.statistics.errors}`} />
                        </ListItem>
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ backgroundColor: theme.palette.error.light, color: theme.palette.error.contrastText }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Error sx={{ mr: 1 }} />
                    <Typography variant="h6">Import Failed</Typography>
                  </Box>
                  <Typography variant="body1">{result.error}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Paper>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Instructions
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          How to prepare your Google Drive folder:
        </Typography>
        <List>
          <ListItem>
            <ListItemText 
              primary="1. Create a Google Drive folder with your content" 
              secondary="The folder should be shared with 'Anyone with the link' access"
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="2. Add your CSV file to the folder" 
              secondary="The CSV should contain columns for Flow, Question type, Text, and other required fields"
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="3. Add your media files to the folder" 
              secondary="Images should be named to match the page numbers in your CSV"
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="4. Copy the folder URL and paste it above" 
              secondary="Click 'Import Content' to start the import process"
            />
          </ListItem>
        </List>
      </Paper>
    </Container>
  );
};

export default ContentImport;
